import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    connect(): void {
        const fields = this.element.querySelectorAll<HTMLSelectElement>('[data-auto-submit]');

        fields.forEach((field) => {
            field.addEventListener('change', () => {
                const form = field.closest('form');
                if (form) {
                    form.requestSubmit();
                }
            });
        });

        const textInputs = this.element.querySelectorAll<HTMLInputElement>('input[type="text"]');

        textInputs.forEach((input) => {
            input.addEventListener('focus', (event) => {
                const el = event.target as HTMLInputElement;

                // petit délai pour compatibilité mobile / Safari
                setTimeout(() => {
                    el.select();
                }, 0);
            });
        });
    }

    // Permet de repositionner l'élément qui aurait changer de hauteur
    scrollToElement() {
        this.element.scrollIntoView({ behavior: "smooth", block: "end" });
    }


    checkIntegerValue(event: KeyboardEvent) {
        const input = event.target as HTMLInputElement;
        const min = parseFloat(input.dataset.min || "");
        const max = parseInt(input.dataset.max || "", 10);
        const scale = parseInt(input.dataset.scale || "", 10);

        let cleanValue = input.value.replace(/[^\d.,]/g, '');
        cleanValue = cleanValue.replace(/,/g, ".");
        let numericValue = parseFloat(cleanValue);

        if (isNaN(numericValue)) {
            
            input.value = '';
            return;
        }

        if (!isNaN(min) && !isNaN(max) && !isNaN(scale)) {
            if (numericValue < min ) {
                numericValue = min;
            }
            else if (numericValue > max ) {
                numericValue = max;
            }
        }

        input.value = this.formatValue(numericValue, scale);
    }

    /**
     * Filtre à la volée les caractères non numériques saisis dans un champ stepper/number
     * (les décimales restent tolérées via data-scale, cf. checkIntegerValue) et fait apparaître
     * les espaces insécables séparateurs de milliers pendant la saisie.
     */
    restrictToDigits(event: InputEvent) {
        const input = event.target as HTMLInputElement;
        const scale = parseInt(input.dataset.scale || "0", 10);
        const allowedPattern = scale > 0 ? /[^\d.,]/g : /[^\d]/g;

        const caret = input.selectionStart ?? input.value.length;
        const digitsBeforeCaret = (input.value.slice(0, caret).match(/\d/g) || []).length;

        const formatted = this.formatThousands(input.value.replace(allowedPattern, ''));

        if (formatted !== input.value) {
            input.value = formatted;

            let seen = 0;
            let pos = formatted.length;
            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) {
                    seen++;
                    if (seen === digitsBeforeCaret) {
                        pos = i + 1;
                        break;
                    }
                }
            }
            if (digitsBeforeCaret === 0) {
                pos = 0;
            }
            input.setSelectionRange(pos, pos);
        }
    }

    /**
     * Formatte un nombre complet (fin de saisie) via Intl (locale fr-FR) : séparateur
     * décimal virgule, regroupement par milliers avec espace insécable inclus.
     */
    private formatValue(numericValue: number, scale: number): string {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: scale,
            maximumFractionDigits: scale,
        }).format(numericValue);
    }

    /**
     * Regroupe la partie entière par milliers via Intl (locale fr-FR), qui insère
     * lui-même l'espace insécable d'usage - pas de caractère à gérer à la main ici.
     * Distinct de `formatValue` : ici la saisie peut être incomplète ("1234," en cours
     * de frappe), donc pas de parseFloat/arrondi possible avant la fin de saisie.
     */
    private formatThousands(value: string): string {
        const [integerPart, decimalPart] = value.split(/[.,]/);
        const grouped = integerPart === '' ? '' : new Intl.NumberFormat('fr-FR').format(BigInt(integerPart));

        return decimalPart !== undefined ? `${grouped},${decimalPart}` : grouped;
    }

    /**
     * Boutons +/- des champs stepper : `data-target` porte l'id du champ à modifier.
     */
    decrement(event: MouseEvent) {
        this.stepValue(event, -1);
    }

    increment(event: MouseEvent) {
        this.stepValue(event, 1);
    }

    private stepValue(event: MouseEvent, direction: number) {
        const button = event.currentTarget as HTMLElement;
        const targetId = button.dataset.target;
        if (!targetId) {
            return;
        }

        const input = document.getElementById(targetId) as HTMLInputElement | null;
        if (!input) {
            return;
        }

        const min = parseFloat(input.dataset.min || "");
        const max = parseFloat(input.dataset.max || "");
        const scale = parseInt(input.dataset.scale || "0", 10);
        const step = Math.pow(10, -scale);

        let current = parseFloat(input.value.replace(/,/g, "."));
        if (isNaN(current)) {
            current = !isNaN(min) ? min : 0;
        } else {
            current += direction * step;
        }

        if (!isNaN(min) && current < min) {
            current = min;
        }
        if (!isNaN(max) && current > max) {
            current = max;
        }

        input.value = this.formatValue(current, scale);
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }
}